/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import type { FieldPath, FieldValues } from "react-hook-form";

import { CustomInput } from "@/components/atoms";

type FormattedInputFieldProps<T extends FieldValues> = {
  field: any;
  nameField: FieldPath<T>;
  typeField?: React.HTMLInputTypeAttribute;
  placeholder?: string;
  parseValue?: (value: string) => any;
  allowDecimals?: boolean;
  validationPattern?: RegExp;
} & React.InputHTMLAttributes<HTMLInputElement>;

function formatWithThousands(value: number | string): string {
  const [intPart, decimalPart] = String(value).split(".");

  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return decimalPart !== undefined
    ? `${formattedInt}.${decimalPart}`
    : formattedInt;
}

export function FormattedInputField<T extends FieldValues>({
  field,
  typeField = "text",
  placeholder,
  parseValue,
  allowDecimals = false,
  validationPattern,
  ...rest
}: FormattedInputFieldProps<T>) {
  const [displayValue, setDisplayValue] = useState<string>("");
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (isFocused) {
      return;
    }
    if (field.value === undefined || field.value === "") {
      setDisplayValue("");
      return;
    }
    setDisplayValue(formatWithThousands(field.value));
  }, [field.value, isFocused]);

  return (
    <CustomInput
      {...rest}
      type={typeField}
      placeholder={placeholder}
      value={displayValue}
      onFocus={() => {
        setIsFocused(true);

        if (field.value !== undefined) {
          setDisplayValue(String(field.value));
        }
      }}
      onChange={e => {
        let raw = e.target.value;
        raw = raw.replace(",", ".");

        // Keep at most one '.' so decimals type/paste reliably (e.g. "123..12" → "123.12").
        const firstDot = raw.indexOf(".");
        if (firstDot !== -1) {
          raw =
            raw.slice(0, firstDot + 1) +
            raw.slice(firstDot + 1).replace(/\./g, "");
        }

        const defaultRegex = allowDecimals ? /^\d*\.?\d*$/ : /^\d*$/;
        const regex = validationPattern || defaultRegex;

        if (raw !== "" && !regex.test(raw)) {
          return;
        }

        setDisplayValue(raw);
        const parsed = parseValue ? parseValue(raw) : raw;
        field.onChange(parsed);
      }}
      onBlur={() => {
        setIsFocused(false);

        if (field.value !== undefined) {
          setDisplayValue(formatWithThousands(field.value));
        }

        field.onBlur();
      }}
    />
  );
}
